// Rotate a tenant's YABBR API key + sender number without touching the DB.
// Master admin picks the domain; a domain admin edits their own tenant.
// The backend live-tests a new key against YABBR before saving it.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const YabbrCredentialsEditor = () => {
  const [me, setMe] = useState(null);
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [cred, setCred] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => {
      const u = r.data.user || r.data;
      setMe(u);
      if (u.role === 'master_admin') {
        axios.get(`${API}/admin/domains`).then((d) => {
          const rows = (d.data.domains || d.data || []).map((x) => ({ id: x.id, name: x.company?.name || x.id }));
          setDomains(rows);
          if (rows[0]) setDomainId(rows[0].id);
        }).catch(() => {});
      } else if (u.domain_id) {
        setDomainId(u.domain_id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!domainId) return;
    axios.get(`${API}/admin/domains/${domainId}/yabbr-credentials`).then((r) => {
      setCred(r.data);
      setFromNumber(r.data.from_number || '');
      setEnabled(!!r.data.enabled);
    }).catch(() => setCred(null));
  }, [domainId]);

  if (!me || !['master_admin', 'domain_admin', 'manager', 'supervisor', 'domain_supervisor'].includes(me.role)) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { from_number: fromNumber, enabled };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const r = await axios.put(`${API}/admin/domains/${domainId}/yabbr-credentials`, payload);
      if (r.data.key_rotated) {
        const kt = r.data.key_test || {};
        if (kt.warning) toast.warning(kt.warning, { duration: 12000 });
        else toast.success(`New key verified against YABBR (${kt.messages_visible ?? 0} messages visible) and saved`);
      } else {
        toast.success('YABBR settings saved');
      }
      setApiKey('');
      const g = await axios.get(`${API}/admin/domains/${domainId}/yabbr-credentials`);
      setCred(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save YABBR credentials');
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="yabbr-credentials-editor">
      <button onClick={() => setOpen(!open)}
        className="text-[11px] font-bold text-slate-600 inline-flex items-center gap-1.5 hover:text-teal-700"
        data-testid="yabbr-creds-toggle">
        <KeyRound className="w-3 h-3" /> {open ? 'Hide' : 'Update'} YABBR API key
        {cred?.key_set && <span className="text-slate-400 font-normal">(current {cred.key_hint})</span>}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {me.role === 'master_admin' && (
            <select value={domainId} onChange={(e) => setDomainId(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1.5"
              data-testid="yabbr-creds-domain-select">
              {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder="Paste new YABBR API key (leave blank to keep current)"
            className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 font-mono"
            data-testid="yabbr-creds-key-input" />
          <div className="flex items-center gap-2">
            <input value={fromNumber} onChange={(e) => setFromNumber(e.target.value)}
              placeholder="Sender number e.g. 61420140090"
              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 font-mono"
              data-testid="yabbr-creds-from-input" />
            <label className="text-[11px] text-slate-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                data-testid="yabbr-creds-enabled-toggle" /> enabled
            </label>
            <button onClick={save} disabled={saving || !domainId}
              className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="yabbr-creds-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save &amp; test
            </button>
          </div>
          <p className="text-[10px] text-slate-400">A new key is live-tested against the YABBR API before saving — a bad key is rejected and the old one kept.</p>
        </div>
      )}
    </div>
  );
};

export default YabbrCredentialsEditor;
